/**
 * "Acknowledge selected" and "Acknowledge all" for notice items (for example
 * "Development Talk Due").
 *
 * 1. Opening takes the ticked rows, or loads every item of the current view
 *    (all pages) for "Acknowledge all". Only items `isBulkAcknowledgeable`
 *    accepts are kept, at most `BATCH_ACKNOWLEDGE_LIMIT` per run.
 * 2. One confirm dialog says how many items of which kind it closes.
 * 3. The run acknowledges each item and ends with one toast.
 */

import { useCallback, useRef, useState, type ReactElement } from 'react';
import { useLingui } from '@lingui/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useBatchTransition, type BatchTransitionSummary } from '@/hooks/useBatchTransition';
import { getCategoryLabel, type WorkflowItemResponse } from '@/types/workflow';
import { isBulkAcknowledgeable } from './inboxListFilters';

/** Items one run acknowledges at most; the rest wait for the next run. */
export const BATCH_ACKNOWLEDGE_LIMIT = 200;

type Item = WorkflowItemResponse & { id: string };

interface AcknowledgePlan {
  loading: boolean;
  fromSelection: boolean;
  items: Item[];
  later: number;
}

export interface BatchAcknowledgeFlow {
  /**
   * Opens the confirm dialog for these items. Pass a loader for "Acknowledge
   * all" so the complete view is read first; pass `fromSelection` for the
   * ticked rows.
   */
  open: (source: Item[] | (() => Promise<Item[]>), options?: { fromSelection?: boolean }) => void;
  /** The confirm dialog; render it once on the page. */
  dialog: ReactElement | null;
  /** True from the click until the run has finished. */
  busy: boolean;
}

export function useBatchAcknowledgeFlow(): BatchAcknowledgeFlow {
  const { i18n } = useLingui();
  const batch = useBatchTransition('acknowledge');
  const [plan, setPlan] = useState<AcknowledgePlan | null>(null);
  // A newer `open` or a close makes an older load stale.
  const loadRef = useRef(0);

  const close = useCallback(() => {
    loadRef.current += 1;
    setPlan(null);
  }, []);

  const open = useCallback(
    (source: Item[] | (() => Promise<Item[]>), options?: { fromSelection?: boolean }) => {
      const run = loadRef.current + 1;
      loadRef.current = run;
      const fromSelection = options?.fromSelection ?? false;
      const settle = (loaded: Item[]) => {
        if (loadRef.current !== run) return;
        const eligible = loaded.filter(isBulkAcknowledgeable);
        const items = eligible.slice(0, BATCH_ACKNOWLEDGE_LIMIT);
        setPlan({ loading: false, fromSelection, items, later: eligible.length - items.length });
      };
      if (Array.isArray(source)) {
        if (source.length === 0) return;
        settle(source);
        return;
      }
      setPlan({ loading: true, fromSelection, items: [], later: 0 });
      source().then(settle, () => {
        if (loadRef.current !== run) return;
        setPlan(null);
        toast.error(i18n._({ id: 'workflow.batchAck.loadFailed', message: 'The items could not be loaded. Try again.' }));
      });
    },
    [i18n],
  );

  const report = useCallback(
    (summary: BatchTransitionSummary) => {
      const raced =
        summary.alreadyHandled.length > 0
          ? i18n._({
              id: 'workflow.batchAck.alreadyHandled',
              message:
                '{count, plural, one {# item was already handled by someone else.} other {# items were already handled by someone else.}}',
              values: { count: summary.alreadyHandled.length },
            })
          : undefined;
      if (summary.failed.length > 0) {
        toast.error(
          i18n._({
            id: 'workflow.batchAck.partial',
            message: '{done} acknowledged, {failed, plural, one {# failed} other {# failed}}',
            values: { done: summary.done.length, failed: summary.failed.length },
          }),
          raced ? { description: raced } : undefined,
        );
        return;
      }
      if (summary.done.length === 0 && raced) {
        toast.info(raced);
        return;
      }
      toast.success(
        i18n._({
          id: 'workflow.batchAck.result',
          message: '{count, plural, one {# item acknowledged} other {# items acknowledged}}',
          values: { count: summary.done.length },
        }),
        raced ? { description: raced } : undefined,
      );
    },
    [i18n],
  );

  const confirm = useCallback(async () => {
    if (!plan || plan.loading || plan.items.length === 0) return;
    const summary = await batch.mutateAsync(plan.items.map((item) => item.id));
    setPlan(null);
    report(summary);
  }, [plan, batch, report]);

  const dialog = plan ? (
    <BatchAcknowledgeDialog plan={plan} onClose={close} onConfirm={confirm} isPending={batch.isPending} />
  ) : null;

  return { open, dialog, busy: batch.isPending || !!plan?.loading };
}

interface BatchAcknowledgeDialogProps {
  plan: AcknowledgePlan;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isPending: boolean;
}

function BatchAcknowledgeDialog({ plan, onClose, onConfirm, isPending }: BatchAcknowledgeDialogProps) {
  const { i18n } = useLingui();
  const count = plan.items.length;

  // How many of each kind, largest first.
  const byCategory = new Map<string, number>();
  for (const item of plan.items) {
    const category = item.category ?? '';
    byCategory.set(category, (byCategory.get(category) ?? 0) + 1);
  }
  const groups = [...byCategory].sort((a, b) => b[1] - a[1]);
  const hasTalkReminders = byCategory.has('portfolio_conversation_due');

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={
        plan.loading
          ? i18n._({ id: 'workflow.batchAck.loading', message: 'Loading the items...' })
          : count === 0
            ? i18n._({ id: 'workflow.batchAck.none', message: 'Nothing to acknowledge' })
            : i18n._({
                id: 'workflow.batchAck.title',
                message: '{count, plural, one {Acknowledge # item?} other {Acknowledge # items?}}',
                values: { count },
              })
      }
      description={
        plan.loading
          ? undefined
          : count === 0
            ? i18n._({
                id: 'workflow.batchAck.noneBody',
                message: 'No item in this view can be acknowledged in bulk.',
              })
            : i18n._({
                id: 'workflow.batchAck.body',
                message: 'Each item closes and moves to Done. This cannot be undone.',
              })
      }
      confirmLabel={
        plan.fromSelection
          ? i18n._({ id: 'workflow.batchAck.confirmSelected', message: 'Acknowledge selected ({count})', values: { count } })
          : i18n._({ id: 'workflow.batchAck.confirmAll', message: 'Acknowledge all ({count})', values: { count } })
      }
      confirmDisabled={plan.loading || count === 0}
      isPending={isPending}
      onConfirm={onConfirm}
    >
      {plan.loading ? (
        <div className="flex justify-center py-6" data-testid="batch-ack-loading">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      ) : count > 0 ? (
        <div className="space-y-3" data-testid="batch-ack-summary">
          <ul className="divide-y divide-border rounded-md border">
            {groups.map(([category, n]) => (
              <li
                key={category}
                className="flex items-center justify-between gap-4 px-3 py-2 text-sm"
                data-testid={`batch-ack-group-${category}`}
              >
                <span>{getCategoryLabel(i18n, category)}</span>
                <span className="font-medium tabular-nums">{n}</span>
              </li>
            ))}
          </ul>
          {hasTalkReminders && (
            <p className="text-xs text-muted-foreground" data-testid="batch-ack-talk-note">
              {i18n._({
                id: 'workflow.batchAck.talkNote',
                message:
                  'A child with no development talk planned or recorded gets a new reminder the next day. Planning the talk closes the reminder by itself.',
              })}
            </p>
          )}
          {plan.later > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="batch-ack-later">
              {i18n._({
                id: 'workflow.batchAck.later',
                message:
                  '{count, plural, one {# more item waits for the next run.} other {# more items wait for the next run.}}',
                values: { count: plan.later },
              })}
            </p>
          )}
        </div>
      ) : null}
    </ConfirmDialog>
  );
}
